import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { CreatePaymentMethodCardData } from '@stripe/stripe-js/types/stripe-js/payment-intents'
import VerticalButtonBox from 'components/buttons/VerticalButtonBox'
import StyledCardElement from 'components/form-elements/StyledCardElement'
import NotificationSys from 'components/NotificationSystem'
import { ReactNode, useState } from 'react'
import { SubscribeResponse, SubscriptionPlanType } from 'types'
import { SubscriptionPlanSelect } from './SubscriptionPlanSelect'

export function SubscriptionPaymentForm({
  title,
  submitLabel,
  billingEmail,
  fixedPlan,
  description,
  onCancel,
  onSubmit,
  onSuccess,
  onPaymentFailed,
}: {
  title: ReactNode
  submitLabel: string
  billingEmail?: string
  /** Set when the user has nothing to choose — the plan selector is hidden and this plan is used. */
  fixedPlan?: SubscriptionPlanType
  description?: ReactNode
  /** Shown only where abandoning makes sense — during registration, not on the paywall. */
  onCancel?: () => void
  onSubmit: (
    paymentMethodId: string,
    plan: SubscriptionPlanType,
  ) => Promise<{ data: SubscribeResponse }>
  onSuccess: (response: SubscribeResponse) => void
  onPaymentFailed: (errorMessage?: string) => void
}) {
  const stripe = useStripe()
  const elements = useElements()

  const [loading, setLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanType | ''>(fixedPlan ?? '')

  const handleSubmit = async () => {
    if (!stripe || !elements) return

    if (!selectedPlan) {
      NotificationSys.showWarning('Please select a subscription plan.')
      return
    }

    try {
      setLoading(true)

      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement) as CreatePaymentMethodCardData['card'],
        ...(billingEmail && { billing_details: { email: billingEmail } }),
      })

      if (result.error) {
        NotificationSys.showWarning(
          <>
            Your payment has been declined.
            <br />
            {result.error.message}
          </>,
        )
        return
      }

      const res = await onSubmit(result.paymentMethod.id, selectedPlan)
      const { stripeClientSecret, stripeErrorMessage } = res.data

      if (stripeErrorMessage) {
        onPaymentFailed(stripeErrorMessage)
        return
      }

      if (stripeClientSecret) {
        const { error } = await stripe.confirmCardPayment(stripeClientSecret)
        if (error) {
          console.error('Payment error:', error)
          onPaymentFailed(error.message)
        } else {
          onSuccess(res.data)
        }
        return
      }

      onSuccess(res.data)
    } finally {
      setLoading(false)
    }
  }

  if (!stripe || !elements) {
    return <div>Loading...</div>
  }

  return (
    <Stack direction="column" spacing={2}>
      {title}

      {description ?? (
        <Typography variant="body2" color="text.secondary">
          Select the subscription plan that works best for you. Your subscription provides full
          access to ProActive Memory Services for Family Caregivers. Plans renew automatically to
          ensure uninterrupted access, and you can manage or update your subscription at any time.
        </Typography>
      )}

      {!fixedPlan && <SubscriptionPlanSelect value={selectedPlan} onChange={setSelectedPlan} />}

      <StyledCardElement />

      <Typography variant="caption" color="text.secondary">
        By providing your card information, you allow the ProActive Memory Services team to charge
        your card for future payments in accordance with their terms.
      </Typography>

      {selectedPlan && (
        <Typography variant="caption" color="text.secondary">
          Final amount includes applicable taxes and fees. Manage your subscription anytime.
        </Typography>
      )}

      {onCancel ? (
        <VerticalButtonBox
          mt={2}
          onSave={handleSubmit}
          saveText={submitLabel}
          loading={loading}
          disabledSave={!stripe || !elements || !selectedPlan}
          onCancel={onCancel}
        />
      ) : (
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="secondary"
            sx={{ minWidth: 200 }}
            disabled={!stripe || !elements || !selectedPlan}
            onClick={handleSubmit}
          >
            {submitLabel}
          </LoadingButton>
        </Box>
      )}
    </Stack>
  )
}
